import { useState, useEffect } from "react";
import { Mail, Instagram, Send, MessageCircle, Facebook } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { useSettings } from "../contexts/SettingsContext";
import { subscribeToFAQs, subscribeToPackages } from "../services/firestore";

const Contact = () => {
  const { contactInfo, socialMedia } = useSettings();
  const [faqs, setFaqs] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    eventDate: "",
    eventDayTime: "",
    location: "",
    package: "",
    notes: "",
  });

  useEffect(() => {
    const unsubscribe = subscribeToFAQs((data) => {
      if (data && data.length > 0) {
        const sortedData = [...data].sort(
          (a, b) => (a.order || 0) - (b.order || 0),
        );
        setFaqs(sortedData);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToPackages((data) => {
      const activePackages = (data || [])
        .filter((pkg) => pkg.isActive !== false)
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
        .map((pkg) => {
          return `${pkg.name}`;
        });

      setPackageOptions(activePackages);
    });

    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const message = `Halo teh, aku mau booking ${formData.package || "henna"} untuk tanggal ${formData.eventDate}

Nama: ${formData.name}
Hari/Jam: ${formData.eventDayTime}
Lokasi: ${formData.location}
Paket: ${formData.package || "-"}
Catatan: ${formData.notes || "-"}`;

    const whatsappNumber = contactInfo.whatsapp || "6289526775445";
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      message,
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  const contactChannels = [
    {
      icon: <Mail size={20} />,
      title: "Email",
      detail: contactInfo.email || "putrileony.office@gmail.com",
      href: contactInfo.email
        ? `mailto:${contactInfo.email}`
        : "mailto:putrileony.office@gmail.com",
      enabled: true,
    },
    {
      icon: <Facebook size={20} />,
      title: "Facebook",
      detail: "Teh Putri Henna",
      href: socialMedia.facebook || "https://facebook.com/tehputrihenna",
      enabled: !!socialMedia.facebook,
    },
    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="currentColor"
        >
          <path d="M19 10.56a6.57 6.57 0 0 1-3.838-1.229v5.588a5.082 5.082 0 1 1-4.38-5.035v2.81a2.33 2.33 0 1 0 1.63 2.225V4h2.75q-.002.35.06.694A3.82 3.82 0 0 0 16.906 7.2c.621.41 1.35.629 2.094.628z" />
        </svg>
      ),
      title: "TikTok",
      detail: socialMedia.tiktok || "@tehputrihenna",
      href: socialMedia.tiktok?.startsWith("http")
        ? socialMedia.tiktok
        : `https://tiktok.com/${socialMedia.tiktok || "@tehputrihenna"}`,
      enabled: !!socialMedia.tiktok,
    },
    {
      icon: <Instagram size={20} />,
      title: "Instagram",
      detail: socialMedia.instagram?.replace("@", "") || "@tehputri_henna",
      href: socialMedia.instagram?.startsWith("http")
        ? socialMedia.instagram
        : `https://instagram.com/${socialMedia.instagram?.replace("@", "") || "tehputri_henna"}`,
      enabled: !!socialMedia.instagram,
    },
  ].filter((c) => c.enabled);

  return (
    <section
      id="contact"
      className="relative py-20 overflow-hidden bg-black sm:py-28 md:py-36"
    >
      {/* Ornate top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF7A]/40 to-transparent" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-1 bg-[#D4AF7A]/30 blur-sm rounded-full" />

      {/* Soft glow */}
      <div className="pointer-events-none absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#D4AF7A]/[0.03] rounded-full blur-[150px]" />

      <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-10 text-center xs:mb-12 md:mb-16">
          <span className="inline-block px-3 xs:px-4 py-1.5 xs:py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-full text-[#D4AF7A] text-xs xs:text-sm font-semibold mb-3 xs:mb-4">
            Hubungi Kami
          </span>
          <h2 className="font-display text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#F7F5F2] mb-4 xs:mb-6">
            Mari Berdiskusi
          </h2>
          <div className="flex items-center justify-center gap-2 mb-4 xs:mb-6 md:mb-8">
            <span className="block w-8 xs:w-10 md:w-14 h-px bg-gradient-to-l from-[#D4AF7A]/50 to-transparent"></span>
            <span className="block w-2 h-2 rotate-45 bg-[#D4AF7A]"></span>
            <span className="block w-16 xs:w-20 md:w-24 h-[2px] bg-gradient-to-r from-[#C9A882] via-[#D4AF7A] to-[#E5C99A]"></span>
            <span className="block w-2 h-2 rotate-45 bg-[#D4AF7A]"></span>
            <span className="block w-8 xs:w-10 md:w-14 h-px bg-gradient-to-r from-[#D4AF7A]/50 to-transparent"></span>
          </div>
          <p className="text-sm xs:text-base md:text-lg text-[#F7F5F2]/70 max-w-2xl mx-auto px-2">
            Konsultasikan kebutuhan henna Anda dengan kami
          </p>
        </div>

        {/* ── 2-Column Grid ── */}
        <div className="grid gap-10 lg:gap-16 lg:grid-cols-2">
          {/* ═══ LEFT: Contact Cards & FAQ ═══ */}
          <div className="space-y-10">
            {/* ── Contact Cards — 2×2 ── */}
            <div className="grid gap-4 sm:grid-cols-2">
              {contactChannels.map((ch, i) => (
                <a
                  key={i}
                  href={ch.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative overflow-hidden rounded-xl border border-[#D4AF7A]/10 bg-[#0A0A0A] p-5 transition-all duration-400 hover:border-[#D4AF7A]/30 hover:bg-[#111] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF7A]/50"
                >
                  {/* Corner ornament */}
                  <span className="absolute top-0 right-0 w-8 h-8">
                    <span className="absolute top-0 right-0 h-5 w-px bg-[#D4AF7A]/20" />
                    <span className="absolute top-0 right-0 h-px w-5 bg-[#D4AF7A]/20" />
                  </span>

                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#D4AF7A]/10 text-[#D4AF7A] transition-all duration-400 group-hover:bg-[#D4AF7A]/20">
                    {ch.icon}
                  </div>
                  <h3 className="text-sm font-semibold text-[#F7F5F2] mb-0.5">
                    {ch.title}
                  </h3>
                  <p className="text-xs text-[#F7F5F2]/40 truncate">
                    {ch.detail}
                  </p>
                </a>
              ))}
            </div>

            {/* ── FAQ ── */}
            {faqs.length > 0 && (
              <div className="border-t border-[#D4AF7A]/10 pt-10">
                <div className="flex items-center gap-3 mb-6">
                  <MessageCircle size={16} className="text-[#D4AF7A]/50" />
                  <h3 className="text-sm font-semibold text-[#F7F5F2]">
                    Pertanyaan Umum
                  </h3>
                </div>
                <Accordion type="single" collapsible className="space-y-2">
                  {faqs.map((faq) => (
                    <AccordionItem
                      key={faq.id}
                      value={`faq-${faq.id}`}
                      className="border border-[#D4AF7A]/10 rounded-xl overflow-hidden transition-all duration-300 data-[state=open]:border-[#D4AF7A]/25"
                    >
                      <AccordionTrigger className="px-4 py-3.5 text-left text-sm font-medium text-[#F7F5F2]/70 transition-all duration-300 hover:text-[#D4AF7A]/90 hover:no-underline data-[state=open]:text-[#D4AF7A]/90">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4 text-sm text-[#F7F5F2]/45 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>

          {/* ═══ RIGHT: Booking Form ═══ */}
          <div>
            <div className="rounded-2xl border border-[#D4AF7A]/15 bg-[#0A0A0A] p-6 sm:p-8 md:p-10">
              {/* Form heading */}
              <div className="mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-[#F7F5F2]">
                  Booking Henna
                </h3>
                <p className="mt-1.5 text-sm text-[#F7F5F2]/40">
                  Isi data berikut untuk konsultasi
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nama */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-[#F7F5F2]/70 mb-1.5"
                  >
                    Nama Lengkap
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-xl border border-[#D4AF7A]/15 bg-black/30 px-4 py-3 text-sm text-[#F7F5F2] placeholder-[#F7F5F2]/20 transition-all duration-300 focus:border-[#D4AF7A]/40 focus:bg-black/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]/15"
                    placeholder="Nama lengkap Anda"
                  />
                </div>

                {/* Tanggal + Hari/Jam */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="eventDate"
                      className="block text-sm font-medium text-[#F7F5F2]/70 mb-1.5"
                    >
                      Tanggal Acara
                    </label>
                    <input
                      id="eventDate"
                      type="date"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      required
                      className="block w-full rounded-xl border border-[#D4AF7A]/15 bg-black/30 px-4 py-3 text-sm text-[#F7F5F2] transition-all duration-300 focus:border-[#D4AF7A]/40 focus:bg-black/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]/15 [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="eventDayTime"
                      className="block text-sm font-medium text-[#F7F5F2]/70 mb-1.5"
                    >
                      Hari / Jam
                    </label>
                    <input
                      id="eventDayTime"
                      type="text"
                      name="eventDayTime"
                      value={formData.eventDayTime}
                      onChange={handleChange}
                      required
                      className="block w-full rounded-xl border border-[#D4AF7A]/15 bg-black/30 px-4 py-3 text-sm text-[#F7F5F2] placeholder-[#F7F5F2]/20 transition-all duration-300 focus:border-[#D4AF7A]/40 focus:bg-black/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]/15"
                      placeholder="Sabtu / 09:00 WIB"
                    />
                  </div>
                </div>

                {/* Lokasi */}
                <div>
                  <label
                    htmlFor="location"
                    className="block text-sm font-medium text-[#F7F5F2]/70 mb-1.5"
                  >
                    Lokasi
                  </label>
                  <input
                    id="location"
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-xl border border-[#D4AF7A]/15 bg-black/30 px-4 py-3 text-sm text-[#F7F5F2] placeholder-[#F7F5F2]/20 transition-all duration-300 focus:border-[#D4AF7A]/40 focus:bg-black/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]/15"
                    placeholder="Alamat lengkap lokasi acara"
                  />
                </div>

                {/* Paket */}
                <div>
                  <label
                    htmlFor="package"
                    className="block text-sm font-medium text-[#F7F5F2]/70 mb-1.5"
                  >
                    Paket Henna
                  </label>
                  <select
                    id="package"
                    name="package"
                    value={formData.package}
                    onChange={handleChange}
                    className="block w-full rounded-xl border border-[#D4AF7A]/15 bg-black/30 px-4 py-3 text-sm text-[#F7F5F2] transition-all duration-300 focus:border-[#D4AF7A]/40 focus:bg-black/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]/15"
                  >
                    <option value="" className="bg-[#0A0A0A] text-[#F7F5F2]/50">
                      Pilih paket (opsional)
                    </option>
                    {packageOptions.map((pkg, index) => (
                      <option
                        key={`${pkg}-${index}`}
                        value={pkg}
                        className="bg-[#0A0A0A] text-[#F7F5F2]"
                      >
                        {pkg}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Catatan */}
                <div>
                  <label
                    htmlFor="notes"
                    className="block text-sm font-medium text-[#F7F5F2]/70 mb-1.5"
                  >
                    Catatan Khusus
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    className="block w-full rounded-xl border border-[#D4AF7A]/15 bg-black/30 px-4 py-3 text-sm text-[#F7F5F2] placeholder-[#F7F5F2]/20 transition-all duration-300 focus:border-[#D4AF7A]/40 focus:bg-black/50 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]/15 resize-none"
                    placeholder="Request atau catatan tambahan..."
                  />
                </div>

                {/* Submit */}
                <div className="pt-2">
                  <button
                    type="submit"
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] px-6 py-3.5 text-sm font-bold text-black transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:shadow-[#D4AF7A]/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF7A] focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                  >
                    Kirim via WhatsApp
                    <Send
                      size={15}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </button>
                  <p className="mt-3 text-center text-xs text-[#F7F5F2]/20">
                    Gratis konsultasi, tim kami akan membalas dalam 1x24 jam
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
